import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { ActivityFileTypeEnum } from '@sport-activities/sdk';
import { add } from 'date-fns';
import { Subject, Subscription } from 'rxjs';

import { Activity } from '~/core/model/activity.model';
import { ActivityMediaResolutionEnum } from '~/organizer/shared/enums/media-resolution.enum';
import { ActivityPlanningDateSelectionInterface } from '~/organizer/activity-planning/components/shared/date-selection/interfaces/date-selection.interface';
import { ActivityPlanningDateSelectionWeekConfigInterface } from '~/organizer/activity-planning/components/shared/date-selection/interfaces/week.config.interface';
import { ActivityPlanningDateSelectionWeekInterface } from '~/organizer/activity-planning/components/shared/date-selection/interfaces/week.interface';
import { ActivitySelectionService } from '~/organizer/activity-planning/services/activity-selection.service';
import { CalendarCreationTimeslotInterface } from '~/organizer/shared/components/shared/calendar/interfaces/recurrent-event.interface';
import { CtaButtonTypeEnum } from '~/component/call-to-action/enums/button-type.enum';
import { FormControlService } from '~/core/services/form/form-control.service';
import { LocaleService } from '~/i18n/services/locale.service';
import { LocalizedDatePipe } from '~/core/pipes/localized-date.pipe';
import { ModalComponent } from '~/component/modal/modal.component';
import { ModalRoleEnum } from '~/component/modal/enums/role.enum';
import { ModalVariantEnum } from '~/component/modal/enums/modal-variant.enum';
import { OrganizerSharedActivityService } from '~/organizer/shared/services/activity/activity.service';
import { OrganizerSharedPageContainerTipConfigInterface } from '~/organizer/shared/components/shared/page-container/interfaces/tip.config.interface';
import { RedirectionService } from '~/core/services/redirection/redirection.service';
import { TitleService } from '~/core/services/title/title.service';
import { ToggleNameEnum } from '~/toggles/services/toggle/enums/name.enum';
import { ToggleService } from '~/toggles/services/toggle.service';
import { VtmnPlayButtonShapeEnum } from '~/component/buttons/vtmn-play-button/enum/vtmn-play-button-shape.enum';
import { VtmnPlayButtonVariantEnum } from '~/component/buttons/vtmn-play-button/enum/vtmn-play-button-variant.enum';
import { VtmnPlayIconTypeEnum } from '~/component/vtmn-play-icon/enums/vtmn-play-icon-type.enum';
import { convertCreationTimeslotsToTimeslotBodyInterface } from '~/organizer/activity-planning/helpers/timeslots/convert-creation-timeslots-to-timeslot-body-interface/convert-creation-timeslots-to-timeslot-body-interface.helper';
import { dateSelectionValidator } from '~/organizer/activity-planning/validators/date-selection.validator';
import {
  isDateSelectionValidRecurrence,
  isDateSelectionValidSingleDate,
} from '~/organizer/activity-planning/helpers/date-selection.helper';
import { removeFalsyValues } from '~/core/helpers/object.helper';
import { setDateTime } from '~/core/helpers/date/date.helper';
import { weekConfig } from '~/organizer/activity-planning/components/shared/date-selection/date-selection.component.config';
import { TipsVariantEnum } from '~/component/tips/enum/variant.enum';

@Component({
  selector: 'app-organizer-activity-planning-activity',
  templateUrl: './activity.component.html',
  styleUrls: ['./activity.component.scss'],
})
export class OrganizerActivityPlanningPageActivityComponent
  implements OnInit, OnDestroy
{
  @ViewChild('timeslotsLimitReachedModal')
  public timeslotsLimitReachedModal: ModalComponent;

  private dateSelectionsSubscription: Subscription;
  private week: ActivityPlanningDateSelectionWeekInterface[] = [];

  private readonly weekConfig: ActivityPlanningDateSelectionWeekConfigInterface[] =
    weekConfig;

  public readonly ctaButtonType = CtaButtonTypeEnum;
  public readonly error$ = new Subject<boolean>();
  public readonly iconTypeEnum = VtmnPlayIconTypeEnum;
  public readonly modalRole = ModalRoleEnum;
  public readonly modalVariant = ModalVariantEnum;
  public readonly toggleNameEnum = ToggleNameEnum;
  public readonly vtmnPlayButtonShape = VtmnPlayButtonShapeEnum;
  public readonly vtmnPlayButtonVariant = VtmnPlayButtonVariantEnum;

  public activity: Activity;
  public dateSelections: FormArray;
  public dateSelectionsTranslated: string[];
  public displayValidationError = false;
  public formGroup: FormGroup = new FormGroup({});

  constructor(
    private readonly activitySelectionService: ActivitySelectionService,
    private readonly activityService: OrganizerSharedActivityService,
    private readonly formBuilder: FormBuilder,
    private readonly formControlService: FormControlService,
    private readonly localeService: LocaleService,
    private readonly localizedDatePipe: LocalizedDatePipe,
    private readonly redirectionService: RedirectionService,
    private readonly titleService: TitleService,
    private readonly toggleService: ToggleService,
    private readonly translateService: TranslateService,
    public readonly layoutElementRef: ElementRef
  ) {}

  public ngOnDestroy(): void {
    if (this.dateSelectionsSubscription) {
      this.dateSelectionsSubscription.unsubscribe();
    }
  }

  public ngOnInit(): void {
    this.titleService.setPageTitle(
      'seo.organizer.activity_planning.activity.title'
    );

    this.activity = this.activityService.activity;

    this.buildWeek();
    this.buildFormGroup();
  }

  public get activityOfferMaxPrice(): number {
    return (
      this.activity?.indicativeOffers?.length > 1 &&
      this.activity?.maxPriceOffer?.price !==
        this.activity?.minPriceOffer?.price &&
      this.activity?.maxPriceOffer?.price
    );
  }

  public get dateLocale(): Locale {
    return this.localeService.dateLocale;
  }

  public get dateSelectionControls(): AbstractControl[] {
    return this.dateSelectionFormArray?.controls;
  }

  public get dateSelectionFormArray(): FormArray {
    return this.formGroup.get('dateSelections') as FormArray;
  }

  public get dateSelectionValues(): ActivityPlanningDateSelectionInterface[] {
    return this.dateSelectionControls
      .map((control) => control.value.dateSelection)
      .filter((dateSelection) => dateSelection);
  }

  public get formattedDateArrayErrors(): string[] {
    return this.formControlService.getFormattedErrors(
      this.getErrors(this.dateSelectionFormArray),
      'dateSelectionArray'
    );
  }

  public get picture(): string {
    return (
      this.activity?.getMediaUrlByType(
        'MAIN_MEDIA' as ActivityFileTypeEnum,
        ActivityMediaResolutionEnum.ICON
      ) || this.activity?.picture
    );
  }

  public get timeslotsLimitModalText(): string {
    return this.translateService.instant(
      'activity_planning.activity.timeslots_limit_modal.text',
      {
        timeslotsLimit: this.getTimeslotsLimitNumber(),
      }
    );
  }

  public getTimeslotsLimitNumber(): number {
    return (
      Number(
        this.toggleService.getVariant(
          ToggleNameEnum.ActivityCreationTimeslotsLimit,
          '200'
        )
      ) || 200
    );
  }

  public get tipsConfig(): OrganizerSharedPageContainerTipConfigInterface[] {
    const tipsConfigArray: OrganizerSharedPageContainerTipConfigInterface[] = [
      {
        descriptionKey: this.dateSelectionsTranslated?.length
          ? this.dateSelectionsTranslated
          : 'activity_planning.activity.information.description',
        headerKey: 'activity_planning.activity.information.header',
        iconName: VtmnPlayIconTypeEnum.Calendar,
      },
    ];

    if (this.isTimeslotsLimitEnabled()) {
      tipsConfigArray.unshift({
        descriptionKey: this.translateService.instant(
          'activity_planning.activity.information.timeslots_limit.text',
          {
            timeslotsLimit: this.getTimeslotsLimitNumber(),
          }
        ),
        headerKey: this.translateService.instant(
          'activity_planning.activity.information.timeslots_limit.title',
          {
            timeslotsLimit: this.getTimeslotsLimitNumber(),
          }
        ),
        variant: TipsVariantEnum.DANGER,
      });
    }

    return tipsConfigArray;
  }

  private buildCreationTimeslots(): CalendarCreationTimeslotInterface[] {
    return this.dateSelectionValues.map((dateSelection) => {
      const endTime = add(
        setDateTime(dateSelection.startDate, dateSelection.startTime),
        this.activity.isoDuration
      );

      const creationTimeslot: CalendarCreationTimeslotInterface = {
        activity: this.activity,
        isRecurrent: false,
        startTime: dateSelection.startTime,
        endTime: `${endTime.getHours()}:${endTime.getMinutes().toString().padStart(2, '0')}`,
        startDate: dateSelection.startDate,
        endDate: this.localizedDatePipe.transform(endTime, 'YYYY-MM-dd'),
        timezone: dateSelection.timezone,
      };

      if (dateSelection.recurrent) {
        return {
          ...creationTimeslot,
          isRecurrent: true,
          daysOfWeek: Object.keys(
            removeFalsyValues(dateSelection.recurrent.days)
          )
            .map((day) => this.week.find((d) => d.formControlName === day))
            .map((day) => day.fullCalendarDayIndex),
          endDate: dateSelection.recurrent.endDate,
        };
      }
      return creationTimeslot;
    });
  }

  private buildFormGroup(): void {
    this.formGroup = this.formBuilder.group({
      dateSelections: this.formBuilder.array([], Validators.required),
    });

    this.dateSelections = this.dateSelectionFormArray;

    this.dateSelectionsSubscription =
      this.dateSelections.valueChanges.subscribe(() => {
        this.setDateSelectionsTranslation();
      });

    // Default value
    const defaultTimeslots = this.activitySelectionService
      .getCreatedTimeslots()
      .map((createdTimeslot) => {
        const isRecurrent = (createdTimeslot.daysOfWeek || [])?.length !== 0;
        const startDate = this.extractDate(createdTimeslot.startDate);
        const endDate = isRecurrent
          ? this.extractDate(createdTimeslot.endDate)
          : null;

        return this.createDateSelection({
          recurrent: isRecurrent
            ? {
                days: {
                  friday: createdTimeslot.daysOfWeek?.includes(5) || false,
                  monday: createdTimeslot.daysOfWeek?.includes(1) || false,
                  saturday: createdTimeslot.daysOfWeek?.includes(6) || false,
                  sunday: createdTimeslot.daysOfWeek?.includes(0) || false,
                  thursday: createdTimeslot.daysOfWeek?.includes(4) || false,
                  tuesday: createdTimeslot.daysOfWeek?.includes(2) || false,
                  wednesday: createdTimeslot.daysOfWeek?.includes(3) || false,
                },
                endDate,
              }
            : null,
          startDate,
          startTime: createdTimeslot.startTime,
        });
      });

    for (const defaultTimeslot of defaultTimeslots) {
      this.dateSelections.push(defaultTimeslot);
    }

    this.dateSelections.updateValueAndValidity();
    this.formGroup.updateValueAndValidity();
  }

  private buildWeek(): void {
    this.week = this.weekConfig.map((config) => ({
      label: this.translateService.instant(config.labelKey),
      formControlName: config.formControlName,
      fullCalendarDayIndex: config.fullCalendarDayIndex,
    }));
  }

  private createDateSelection(value?: object): FormGroup {
    return this.formBuilder.group({
      dateSelection: [value, dateSelectionValidator()],
    });
  }

  public addDateSelection(): void {
    this.dateSelections.push(this.createDateSelection());
  }

  public deleteCard(index: number): void {
    this.dateSelections.removeAt(index);
  }

  public extractDate(input: Date | string | number): string {
    // one of the old use-cases, i don't really know how
    // we could reach that but hey, who knows
    if (typeof input === 'string' && isNaN(Date.parse(input))) {
      return '';
    }

    // if not a date but parsable, transform to date
    if (!(input instanceof Date)) {
      input = new Date(input);
    }

    // we have to decompose date manually to avoid timezone problems
    return `${input.getFullYear()}-${input.getMonth() + 1}-${input.getDate()}`;
  }

  public getErrors(control: FormControl | FormArray): ValidationErrors {
    return this.displayValidationError && control.errors;
  }

  public isTimeslotsLimitEnabled(): boolean {
    return this.toggleService.isEnabled(
      ToggleNameEnum?.ActivityCreationTimeslotsLimit
    );
  }

  public setDateSelectionsTranslation(): void {
    this.dateSelectionsTranslated = this.dateSelectionValues
      .map((dateSelection) => {
        if (isDateSelectionValidRecurrence(dateSelection)) {
          const days = dateSelection.recurrent.days;
          const daysTranslated = Object.keys(days)
            .filter((day) => days[day])
            .map((day) => this.week.find((d) => d.formControlName === day))
            .map((day) => day.label)
            .join(', ');
          const startDateTranslated = this.localizedDatePipe.transform(
            dateSelection.startDate,
            'longDate'
          );
          const endDateTranslated = this.localizedDatePipe.transform(
            dateSelection.recurrent.endDate,
            'longDate'
          );
          return this.translateService.instant(
            'activity_planning.activity.information.timeslots.recurrent',
            {
              days: daysTranslated,
              startDate: startDateTranslated,
              endDate: endDateTranslated,
            }
          );
        } else if (isDateSelectionValidSingleDate(dateSelection)) {
          const startDateTranslated = this.localizedDatePipe.transform(
            dateSelection.startDate,
            'EEEE dd LLLL y'
          );

          const startTimeFormatted = new Date(
            `${dateSelection.startDate} ${dateSelection.startTime}`
          ).toLocaleTimeString(this.translateService.currentLang, {
            hour: 'numeric',
            minute: 'numeric',
          });

          return this.translateService.instant(
            'activity_planning.activity.information.timeslots.individual',
            {
              startDate: startDateTranslated,
              startTime: startTimeFormatted,
            }
          );
        }
        return '';
      })
      .filter((dateSelectionTranslated) => dateSelectionTranslated);
  }

  public submit(): Promise<boolean> {
    if (this.formGroup.invalid) {
      this.displayValidationError = true;
      return;
    }

    const creationTimeslots = this.buildCreationTimeslots();

    if (this.isTimeslotsLimitEnabled()) {
      const totalTimeslots = convertCreationTimeslotsToTimeslotBodyInterface(
        creationTimeslots,
        this.translateService.currentLang,
        'paid',
        'EUR'
      )?.length;

      if (totalTimeslots > this.getTimeslotsLimitNumber()) {
        this.timeslotsLimitReachedModal.show();
        return;
      }
    }

    this.activitySelectionService.setCreatedTimeslots(creationTimeslots);

    return this.redirectionService.redirect(
      'organizer.activity-planning.my-planning-validation',
      {
        queryParamsHandling: 'preserve',
      }
    );
  }
}
